"use client";

import { Button, Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useRouter } from "next/navigation";

export default function Pricing() {
  return (
    <section id="pricing">
      <div className="container mx-auto px-4 pt-16">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold mb-4">
            SEO analysis pricing plans
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-xl">
            Choose the right SEO analysis plan for your needs: Start with our
            free plan for basic website analysis or upgrade to unlock advanced
            AI-powered SEO features and unlimited projects.
          </p>
        </div>

        <TwoCards />
      </div>
    </section>
  );
}

export function TwoCards() {
  const router = useRouter();

  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:p-8">
      {/* Free Plan */}
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-col gap-2 p-6">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">Free Plan</h2>
            <span className="px-2 py-1 text-xs opacity-50">No credit card</span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-3xl font-bold">€0</span>
            <span className="text-small text-default-500">/forever</span>
          </div>
        </CardHeader>
        <Divider />
        <CardBody className="gap-6">
          <div className="flex flex-col gap-4">
            <FeatureItem text="Single project" isIncluded={true} />
            <FeatureItem text="Basic AI models" isIncluded={true} />
            <FeatureItem text="Unlimited website analysis" isIncluded={true} />
            <FeatureItem text="No DataForSEO® insights" isIncluded={false} />
            <FeatureItem
              text="No AI Chat with recommended answers"
              isIncluded={false}
            />
            <FeatureItem text="No content monitoring" isIncluded={false} />
          </div>
          <Button
            className="w-full mt-2"
            color="primary"
            variant="bordered"
            onPress={() => router.push("/#auth")}
          >
            Get Started
          </Button>
        </CardBody>
      </Card>

      {/* Paid Plan */}
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-col gap-2 p-6">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">All-access plan</h2>
            <span className="px-2 py-1 text-tiny bg-primary-100 text-primary rounded-full">
              Most popular
            </span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-3xl font-bold line-through text-gray-400 mr-2">
              €19.99
            </span>
            <span className="text-3xl font-bold text-[#B91C1C]">€9.99</span>
            <span className="text-small text-default-500">/month</span>
          </div>
          <div className="mt-1">
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
              Limited time offer
            </span>
          </div>
          <p className="text-xs mt-2">
            Grab coupon 👉{" "}
            <span
              className="font-mono bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded cursor-pointer"
              onClick={() => {
                window.open(
                  "https://www.uneed.best/tool/seo-ai-pal?tab=deals",
                  "_blank"
                );
              }}
            >
              uneed.best/tool/seo-ai-pal
            </span>
          </p>
        </CardHeader>
        <Divider />
        <CardBody className="gap-6">
          <div className="flex flex-col gap-4">
            <FeatureItem
              text="Unlimited projects"
              isIncluded={true}
              isPremium={true}
            />
            <FeatureItem
              text="Premium AI models"
              isIncluded={true}
              isPremium={true}
            />
            <FeatureItem
              text="Unlimited website analysis"
              isIncluded={true}
              isPremium={true}
            />
            <FeatureItem
              text="Process DataForSEO® insights"
              isIncluded={true}
              isPremium={true}
            />
            <FeatureItem
              text="AI Chat with recommended answers"
              isIncluded={true}
              isPremium={true}
            />
            <FeatureItem
              text="Content monitoring"
              isIncluded={true}
              isPremium={true}
            />
          </div>
          <Button
            className="w-full mt-2"
            color="primary"
            onPress={() => router.push("/#auth")}
          >
            Upgrade Now
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}

interface FeatureItemProps {
  text: string;
  isIncluded: boolean;
  isPremium?: boolean;
}

function FeatureItem({
  text,
  isIncluded,
  isPremium = false,
}: FeatureItemProps) {
  return (
    <div className="flex items-center gap-2">
      {isIncluded ? (
        <IconCheck
          className={isPremium ? "text-primary" : "text-success"}
          width={20}
        />
      ) : (
        <IconX className="text-default-400" width={20} />
      )}
      <span className={!isIncluded ? "text-default-400" : ""}>{text}</span>
    </div>
  );
}
